import React, { useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  FileImage,
  ImagePlus,
  Search,
  ShieldCheck,
  Sparkles,
  UploadCloud,
} from 'lucide-react';
import type { BubbleStyle, ShapeStyle, StoryElement } from '../types';
import { useEditorStore } from '../store/editor';
import {
  bubblePresets,
  characterAssets,
  propAssets,
  sceneAssets,
  shapePresets,
  textPresets,
} from '../model/catalog';
import {
  createBubbleElement,
  createCharacterElement,
  createImageElement,
  createPropElement,
  createShapeElement,
  createTextElement,
  getCanvasSize,
} from '../model/factories';
import { BubbleArt, CharacterArt, PropArt, SceneArt, ShapeArt } from '../render/Artwork';

const titles = {
  scenes: ['Original starter library', 'Scenes'],
  characters: ['Poseable people', 'Characters'],
  props: ['Objects and symbols', 'Props'],
  text: ['Typography presets', 'Text'],
  bubbles: ['Dialogue and captions', 'Bubbles'],
  shapes: ['Diagram building blocks', 'Shapes'],
  upload: ['Private local media', 'Uploads'],
} as const;

const fileToDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });

const loadImageSize = (dataUrl: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth || 1000, height: image.naturalHeight || 1000 });
    image.onerror = () => reject(new Error('The selected image could not be decoded.'));
    image.src = dataUrl;
  });

const sanitiseSvg = (source: string): string => {
  const cleaned = DOMPurify.sanitize(source, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['script', 'foreignObject', 'iframe', 'object', 'embed', 'audio', 'video'],
    FORBID_ATTR: ['onload', 'onclick', 'onerror', 'onmouseover', 'onfocus'],
  });
  const parser = new DOMParser();
  const documentNode = parser.parseFromString(cleaned, 'image/svg+xml');
  documentNode.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      if ((name === 'href' || name.endsWith(':href')) && !value.startsWith('#') && !value.startsWith('data:image/')) {
        node.removeAttribute(attribute.name);
      }
      if (name === 'style' && /url\s*\(\s*['"]?(https?:|\/\/)/i.test(attribute.value)) {
        node.removeAttribute(attribute.name);
      }
    });
  });
  const root = documentNode.documentElement;
  root.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  return new XMLSerializer().serializeToString(root);
};

export const AssetPanel: React.FC = () => {
  const project = useEditorStore((state) => state.project);
  const activeCellId = useEditorStore((state) => state.activeCellId);
  const category = useEditorStore((state) => state.category);
  const addElement = useEditorStore((state) => state.addElement);
  const mutate = useEditorStore((state) => state.mutate);
  const [query, setQuery] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const header = titles[category];
  const activeCell = project?.cells.find((cell) => cell.id === activeCellId) ?? project?.cells[0];
  const canvas = project ? getCanvasSize(project.layout.aspectRatio) : { width: 1600, height: 900 };
  const normalizedQuery = query.trim().toLowerCase();

  const filteredScenes = useMemo(() => sceneAssets.filter((item) => !normalizedQuery || [item.name, ...item.keywords].join(' ').toLowerCase().includes(normalizedQuery)), [normalizedQuery]);
  const filteredCharacters = useMemo(() => characterAssets.filter((item) => !normalizedQuery || [item.name, ...item.keywords].join(' ').toLowerCase().includes(normalizedQuery)), [normalizedQuery]);
  const filteredProps = useMemo(() => propAssets.filter((item) => !normalizedQuery || [item.name, ...item.keywords].join(' ').toLowerCase().includes(normalizedQuery)), [normalizedQuery]);

  const addAtCenter = (element: StoryElement) => {
    element.x = Math.max(20, canvas.width / 2 - element.width / 2);
    element.y = Math.max(20, canvas.height / 2 - element.height / 2);
    addElement(element);
  };

  const chooseScene = (assetId: string) => {
    if (!activeCell) return;
    mutate((draft) => {
      const cell = draft.cells.find((item) => item.id === activeCell.id);
      if (cell) cell.scene.assetId = assetId;
    });
  };

  const uploadFile = async (file: File) => {
    setUploadError('');
    if (file.size > 15 * 1024 * 1024) {
      setUploadError('Please choose an image smaller than 15 MB.');
      return;
    }
    if (!file.type.startsWith('image/') && !file.name.toLowerCase().endsWith('.svg')) {
      setUploadError('StoryboardSmith accepts PNG, JPEG, WebP, GIF, and SVG images.');
      return;
    }
    setUploading(true);
    try {
      let dataUrl: string;
      let size: { width: number; height: number };
      const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
      if (isSvg) {
        const clean = sanitiseSvg(await file.text());
        const blob = new Blob([clean], { type: 'image/svg+xml' });
        dataUrl = await fileToDataUrl(blob);
        size = await loadImageSize(dataUrl);
      } else {
        dataUrl = await fileToDataUrl(file);
        size = await loadImageSize(dataUrl);
      }
      const element = createImageElement(dataUrl, file.name.replace(/\.[^.]+$/, ''), size.width, size.height, file.name);
      addAtCenter(element);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'The image could not be added.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="asset-panel" aria-label={`${header[1]} library`}>
      <header className="asset-panel-header">
        <span className="eyebrow">{header[0]}</span>
        <h2>{header[1]}</h2>
        {category !== 'upload' && (
          <label className="asset-search">
            <Search size={18} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${header[1].toLowerCase()}…`} />
          </label>
        )}
      </header>

      <div className="asset-panel-scroll">
        {category === 'scenes' && (
          <div className="asset-grid scene-grid">
            {filteredScenes.map((asset) => (
              <button key={asset.id} type="button" className={`asset-card ${activeCell?.scene.assetId === asset.id ? 'is-active' : ''}`} onClick={() => chooseScene(asset.id)} title={asset.name}>
                <span className="asset-thumb scene-thumb">
                  <svg viewBox="0 0 1600 900" aria-hidden="true"><SceneArt scene={{ assetId: asset.id, time: activeCell?.scene.time ?? 'day', weather: 'clear', intensity: 0.7 }} width={1600} height={900} prefix={`thumb-${asset.id}`} /></svg>
                </span>
                <span>{asset.name}</span>
              </button>
            ))}
          </div>
        )}

        {category === 'characters' && (
          <div className="asset-grid character-grid">
            {filteredCharacters.map((asset) => (
              <button key={asset.id} type="button" className="asset-card" onClick={() => addAtCenter(createCharacterElement(asset, 0, 0))} title={`Add ${asset.name}`}>
                <span className="asset-thumb character-thumb">
                  <svg viewBox="0 0 200 360" aria-hidden="true"><CharacterArt assetId={asset.id} style={asset.defaultStyle} /></svg>
                </span>
                <span>{asset.name}</span>
              </button>
            ))}
          </div>
        )}

        {category === 'props' && (
          <div className="asset-grid prop-grid">
            {filteredProps.map((asset) => (
              <button key={asset.id} type="button" className="asset-card" onClick={() => addAtCenter(createPropElement(asset.id, asset.name, asset.aspect, 0, 0, 240))} title={`Add ${asset.name}`}>
                <span className="asset-thumb prop-thumb">
                  <svg viewBox="0 0 200 160" aria-hidden="true"><PropArt assetId={asset.id} /></svg>
                </span>
                <span>{asset.name}</span>
              </button>
            ))}
          </div>
        )}

        {category === 'text' && (
          <div className="preset-list">
            {textPresets.filter((item) => !normalizedQuery || `${item.name} ${item.text}`.toLowerCase().includes(normalizedQuery)).map((preset) => (
              <button key={preset.id} type="button" className="text-preset" onClick={() => {
                const element = createTextElement(preset.text, 0, 0, preset.id === 'body' ? 720 : 620, preset.id === 'body' ? 220 : 150);
                element.textStyle = { ...element.textStyle!, fontSize: preset.size, fontWeight: preset.weight, align: preset.id === 'body' ? 'left' : 'center' };
                addAtCenter(element);
              }}>
                <span style={{ fontSize: Math.min(30, 12 + preset.size / 4), fontWeight: preset.weight }}>{preset.text}</span>
                <small>{preset.name}</small>
              </button>
            ))}
          </div>
        )}

        {category === 'bubbles' && (
          <div className="asset-grid bubble-grid">
            {bubblePresets.filter((item) => !normalizedQuery || item.name.toLowerCase().includes(normalizedQuery)).map((preset) => {
              const sample = createBubbleElement(preset.id as BubbleStyle, preset.id === 'caption' ? 'A short caption' : 'Your words here', 0, 0);
              return (
                <button key={preset.id} type="button" className="asset-card bubble-card" onClick={() => addAtCenter(createBubbleElement(preset.id as BubbleStyle))}>
                  <span className="asset-thumb bubble-thumb"><svg viewBox={`0 0 ${sample.width} ${sample.height}`} aria-hidden="true"><BubbleArt element={sample} /></svg></span>
                  <span>{preset.name}</span>
                </button>
              );
            })}
          </div>
        )}

        {category === 'shapes' && (
          <div className="asset-grid shape-grid">
            {shapePresets.filter((item) => !normalizedQuery || item.name.toLowerCase().includes(normalizedQuery)).map((preset) => {
              const sample = createShapeElement(preset.id as ShapeStyle, 0, 0);
              sample.width = 220;
              sample.height = 150;
              return (
                <button key={preset.id} type="button" className="asset-card" onClick={() => addAtCenter(createShapeElement(preset.id as ShapeStyle))}>
                  <span className="asset-thumb shape-thumb"><svg viewBox="0 0 220 150" aria-hidden="true"><ShapeArt element={sample} /></svg></span>
                  <span>{preset.name}</span>
                </button>
              );
            })}
          </div>
        )}

        {category === 'upload' && (
          <div className="upload-panel">
            <div className="upload-hero">
              <span className="upload-icon"><UploadCloud size={34} /></span>
              <h3>Use your own artwork</h3>
              <p>Images are processed and stored locally in this browser. They are never uploaded to StoryboardSmith.</p>
              <button type="button" className="primary-button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <ImagePlus size={18} /> {uploading ? 'Adding image…' : 'Choose image'}
              </button>
              <input ref={fileRef} className="visually-hidden" type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg" onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void uploadFile(file);
              }} />
              {uploadError && <p className="form-error" role="alert">{uploadError}</p>}
            </div>
            <div className="upload-note"><ShieldCheck size={20} /><div><strong>Safer SVG handling</strong><span>Scripts, event handlers, embedded pages, and remote image links are removed before insertion.</span></div></div>
            <div className="upload-note"><FileImage size={20} /><div><strong>Supported formats</strong><span>PNG, JPEG, WebP, GIF, and sanitised SVG up to 15 MB.</span></div></div>
            <div className="upload-note"><Sparkles size={20} /><div><strong>Portable exports</strong><span>Uploaded images remain embedded when you download a .smithboard project.</span></div></div>
          </div>
        )}
      </div>

      {category !== 'upload' && (
        <footer className="asset-panel-footer">
          <ImagePlus size={18} />
          <div><strong>Use your own image</strong><span>Open Upload to add local PNG, JPEG, WebP, GIF, or SVG artwork.</span></div>
        </footer>
      )}
    </section>
  );
};
